import { TabsContent, FormField } from '@/components/molecules';
import { Heading, Button, Input, Switch, Paragraph } from '@/components/atoms';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useChangePassword, useUser } from '@/api';
import { TwoFactorModal, Disable2FAModal } from '../2fa-modal';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  changePasswordSchema,
  type ChangePasswordFormData,
} from '@/validation-schema';

export const SecurityTab = () => {
  const { data: user } = useUser('me');
  const [enable2FA, setEnable2FA] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);

  useEffect(() => {
    if (user?.auth) {
      setEnable2FA(user.auth.isTwoAuthFactorEnabled || user.auth.twoFactorAuth);
    }
  }, [user]);

  const { mutate: changePassword, isPending } = useChangePassword(() => {
    reset();
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<ChangePasswordFormData>({
    resolver: yupResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: {
      password: '',
      newPassword: '',
    },
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    const { password, newPassword } = data;
    if (password && newPassword) {
      changePassword({ password, newPassword });
    }
  };

  const handle2FAToggle = (checked: boolean) => {
    if (checked) {
      setShow2FAModal(true);
    } else {
      setShowDisableModal(true);
    }
  };

  const handle2FAModalClose = () => {
    setShow2FAModal(false);
  };

  const handleDisableModalClose = () => {
    setShowDisableModal(false);
  };

  return (
    <TabsContent value="security">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 max-w-xl mx-auto py-6"
      >
        <Paragraph size="sm" className="text-gray-500 font-medium">
          Manage your account protection settings, including password updates
          and verification options, to keep your store and customer data secure
        </Paragraph>
        <div className="flex flex-col gap-2">
          <FormField label="Current password" error={errors.password?.message}>
            <Input
              type="password"
              placeholder="Enter current password"
              {...register('password')}
            />
          </FormField>

          <FormField label="New Password" error={errors.newPassword?.message}>
            <Input
              type="password"
              placeholder="Enter new password"
              {...register('newPassword')}
            />
          </FormField>

          <div className="flex items-center justify-between pt-4">
            <Heading size="sm">Enable 2FA</Heading>
            <Switch
              checked={enable2FA || user?.auth.isTwoAuthFactorEnabled}
              onCheckedChange={handle2FAToggle}
            />
          </div>
        </div>

        <Button
          className="w-full"
          label="Save"
          size="lg"
          type="submit"
          disabled={!isDirty}
          isLoading={isPending}
        />
      </form>

      <TwoFactorModal
        isOpen={show2FAModal}
        onClose={handle2FAModalClose}
        onSuccess={() => setEnable2FA(true)}
        userEmail={user?.auth?.email || ''}
      />

      <Disable2FAModal
        isOpen={showDisableModal}
        onClose={handleDisableModalClose}
        onSuccess={() => setEnable2FA(false)}
      />
    </TabsContent>
  );
};
